import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { Box, Button, Card, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import strings from 'common/Translation/Translate';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { ReactNode } from 'react';

dayjs.extend(customParseFormat);

const TIME_FORMATS = ['HH:mm:ss', 'HH:mm', 'H:mm', 'h:mm A', 'h:mm a', 'hh:mm A'];

const formatBeoTime = (time: unknown): string => {
  if (time == null || time === '') return '';
  const raw = String(time).trim();
  const strict = dayjs(raw, TIME_FORMATS, true);
  if (strict.isValid()) return strict.format('h:mm A');
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed.format('h:mm A') : raw;
};

export const formatBeoDate = (date: unknown): string => {
  const d = dayjs(date as string);
  return d.isValid() ? d.format('D MMM YYYY') : '';
};

export const formatTimeRange = (event: any): string => {
  const start = formatBeoTime(event?.startTime);
  const end = formatBeoTime(event?.endTime);
  if (start && end) return `${start} – ${end}`;
  const slot = event?.timeSlot ? String(event.timeSlot) : '';
  const parts = slot.split(/\s*[-–]\s*/).filter(Boolean);
  if (parts.length >= 2) {
    const a = formatBeoTime(parts[0]);
    const b = formatBeoTime(parts.slice(1).join(' - '));
    if (a && b) return `${a} – ${b}`;
  }
  return slot;
};

export const eventStartMinutes = (event: any): number => {
  const raw = event?.startTime || String(event?.timeSlot || '').split(/\s*[-–]\s*/)[0];
  if (!raw) return 0;
  const strict = dayjs(String(raw).trim(), TIME_FORMATS, true);
  if (strict.isValid()) return strict.hour() * 60 + strict.minute();
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed.hour() * 60 + parsed.minute() : 0;
};

const joinMeta = (parts: Array<string | null | undefined>) => parts.filter(Boolean).join(' · ');

const guestsLabel = (count: unknown) => {
  if (count == null || count === '') return '';
  return (strings.beo_guests_count || '{count} guests').replace('{count}', String(count));
};

const layoutName = (event: any) => {
  const layout = event?.selectedLayout;
  const name = typeof layout === 'object' ? layout?.label : layout;
  if (!name || name === '-') return '';
  return String(name);
};

const taxSuffix = (raw?: string | null) => {
  if (!raw) return '';
  let value = String(raw).trim().replace(/^\+\s*/, '');
  value = value.replace(/(\d+(?:\.\d+)?%)(?:\s+\1)+/g, '$1').trim();
  const rateFirst = value.match(/^(\d+(?:\.\d+)?)%\s+(.+)$/);
  if (rateFirst) return `${rateFirst[1]}% ${rateFirst[2]}`;
  const nameFirst = value.match(/^(.*?)\s+(\d+(?:\.\d+)?)%$/);
  if (nameFirst) return `${nameFirst[2]}% ${nameFirst[1]}`;
  return value;
};

const HeaderMeta = ({
  primary,
  secondary,
  gap = 0,
}: {
  primary?: ReactNode;
  secondary?: ReactNode;
  gap?: number;
}) => {
  if (!primary && !secondary) return null;
  return (
    <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap }}>
      {primary}
      {secondary}
    </Box>
  );
};

const BoldText = ({ children }: { children: ReactNode }) => (
  <Typography sx={{ fontWeight: 700, fontSize: '0.8125rem', lineHeight: 1.3, wordBreak: 'break-word' }}>
    {children}
  </Typography>
);

const AmountWithTax = ({
  label,
  amount,
  tax,
}: {
  label: string;
  amount?: string | null;
  tax?: string | null;
}) => {
  if (!amount) return null;
  const suffix = taxSuffix(tax);
  const percentMatch = amount ? String(amount).match(/^(.*?)(\s*%)\s*$/) : null;
  const displayAmount = percentMatch ? percentMatch[1] : amount;
  const showPercent = Boolean(percentMatch);
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', fontSize: '0.6875rem', lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Stack direction="row" alignItems="baseline" spacing={0.35} sx={{ minWidth: 0, flexWrap: 'wrap' }}>
        {displayAmount ? (
          <Stack direction="row" alignItems="baseline" spacing={0.2} sx={{ flexShrink: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.8125rem', lineHeight: 1.25, whiteSpace: 'nowrap' }}>
              {displayAmount}
            </Typography>
            {showPercent ? (
              <Typography component="span" color="text.secondary" sx={{ fontSize: '0.6875rem', fontWeight: 500, lineHeight: 1.25, whiteSpace: 'nowrap' }}>
                %
              </Typography>
            ) : null}
          </Stack>
        ) : null}
        {suffix ? (
          <Typography
            component="span"
            color="text.secondary"
            sx={{ fontSize: '0.6875rem', fontWeight: 500, lineHeight: 1.25, whiteSpace: 'nowrap' }}>
            +{suffix}
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );
};

type EventDetailCardProps = {
  event: any;
  readOnly: boolean;
  canDelete: boolean;
  onDelete: () => void;
  onViewAddOns: () => void;
};

const EventDetailCard = ({
  event,
  readOnly,
  canDelete,
  onDelete,
  onViewAddOns,
}: EventDetailCardProps) => {
  const venue = event.venue;
  const eventType = event.eventType && String(event.eventType).trim();
  const eventSubtype = event.eventSubtype && String(event.eventSubtype).trim();
  const functionLine = joinMeta([eventSubtype, eventType && eventType !== eventSubtype ? eventType : '']);
  const dateLabel = formatBeoDate(event.eventDate);
  const timeRange = formatTimeRange(event);
  const guests = guestsLabel(event.guaranteedGuests);
  const seating = layoutName(event);
  const commercials = event?.commercials || {};
  const hourly = Boolean(commercials.hourlyPricing);
  const priceLabel = hourly ? strings.beo_event_price_per_hour : strings.beo_event_price_per_person;
  const priceValue = hourly ? commercials.pricePerHour : commercials.pricePerPerson;
  const addOns = commercials.addOns || [];
  const addOnPrice = commercials.addOnPrice;
  const addOnCount = addOns.length;
  const viewAddOnsLabel = (addOnCount === 1 ? strings.beo_event_view_n_add_on : strings.beo_event_view_n_add_ons).replace(
    '{count}',
    String(addOnCount),
  );
  const lgColumns = [
    'minmax(0, 1.2fr)',
    'minmax(0, 0.8fr)',
    'minmax(0, 0.7fr)',
    'minmax(0, 2.4fr)',
    !readOnly ? 'auto' : null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        bgcolor: readOnly ? 'action.hover' : 'background.paper',
        overflow: 'hidden',
      }}>
      <Box sx={{ px: 1.5, py: 1 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: lgColumns,
            },
            columnGap: { xs: 1.25, lg: 2 },
            rowGap: 0.75,
            alignItems: 'center',
            '& > *:nth-of-type(2), & > *:nth-of-type(3), & > *:nth-of-type(4)': {
              pl: { lg: 1.5 },
              borderLeft: { lg: '1px solid' },
              borderColor: { lg: 'divider' },
            },
            ...(!readOnly
              ? {
                  '& > *:last-of-type': {
                    pl: { lg: 1.5 },
                    borderLeft: { lg: '1px solid' },
                    borderColor: { lg: 'divider' },
                  },
                }
              : {}),
          }}>
            <Box sx={{ minWidth: 0 }}>
              {venue ? (
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.9375rem',
                    lineHeight: 1.25,
                    color: 'primary.dark',
                    wordBreak: 'break-word',
                  }}>
                  {venue}
                </Typography>
              ) : null}
              {functionLine ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
                  {functionLine}
                </Typography>
              ) : null}
            </Box>
            <HeaderMeta
              primary={dateLabel ? (
                <Typography
                  color="text.secondary"
                  sx={{ display: 'block', fontSize: '0.8125rem', fontWeight: 400, lineHeight: 1.25 }}>
                  {dateLabel}
                </Typography>
              ) : null}
              secondary={timeRange ? (
                <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', lineHeight: 1.3, wordBreak: 'break-word' }}>
                  {timeRange}
                </Typography>
              ) : null}
            />
            <HeaderMeta
              primary={guests ? <BoldText>{guests}</BoldText> : null}
              secondary={
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    lineHeight: 1.3,
                    color: seating ? 'text.secondary' : 'warning.dark',
                  }}>
                  {seating || strings.beo_event_seating_not_set}
                </Typography>
              }
            />
            <Box
              sx={{
                minWidth: 0,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, minmax(0, 1fr))' },
                columnGap: 1.5,
                rowGap: 1,
                alignItems: 'start',
              }}>
              <Box sx={{ minWidth: 0 }}>
                <AmountWithTax label={priceLabel} amount={priceValue} tax={commercials.guestChargeTax} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <AmountWithTax label={strings.beo_billing_details_minimum_revenue} amount={commercials.minimumRevenue} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', fontSize: '0.6875rem', lineHeight: 1.2 }}>
                  {strings.beo_event_add_ons}
                </Typography>
                {addOnCount > 0 && addOnPrice ? (
                  <Typography sx={{ fontWeight: 700, fontSize: '0.8125rem', lineHeight: 1.25, wordBreak: 'break-word' }}>
                    {addOnPrice}
                  </Typography>
                ) : (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', fontSize: '0.75rem', lineHeight: 1.3 }}>
                    {strings.beo_event_no_add_ons}
                  </Typography>
                )}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <AmountWithTax
                  label={strings.beo_billing_details_hall_rental}
                  amount={commercials.hallRental}
                  tax={commercials.hallRentalTax}
                />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <AmountWithTax label={strings.beo_event_service_charge} amount={commercials.serviceCharge} />
              </Box>
              {addOnCount > 0 ? (
                <Button
                  variant="text"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewAddOns();
                  }}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    minWidth: 0,
                    px: 0,
                    py: 0,
                    justifyContent: 'flex-start',
                    alignSelf: 'center',
                    fontSize: '0.6875rem',
                    lineHeight: 1.2,
                    color: 'text.secondary',
                  }}>
                  {viewAddOnsLabel}
                </Button>
              ) : (
                <Box />
              )}
            </Box>
            {!readOnly && (
              <Stack
                direction="row"
                spacing={0}
                alignItems="center"
                justifyContent="flex-end"
                sx={{ flexShrink: 0, width: 32, minWidth: 32 }}>
                {canDelete ? (
                  <Tooltip title={strings.deleteText || 'Delete'}>
                    <IconButton
                      size="small"
                      aria-label="delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete();
                      }}
                      sx={{
                        p: 0.5,
                        color: 'text.secondary',
                        '&:hover': { color: 'error.main' },
                      }}>
                      <DeleteOutlineOutlinedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <IconButton size="small" disabled sx={{ p: 0.5, visibility: 'hidden' }} tabIndex={-1} aria-hidden>
                    <DeleteOutlineOutlinedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                )}
              </Stack>
            )}
        </Box>
      </Box>
    </Card>
  );
};

export default EventDetailCard;
